import { IResult } from '../models/HttpResult';
import { IPagedResult } from '../models/HttpPagedResult';
import {
    SyncAnalysis,
    SyncAnalysisFinding,
    SyncAnalysisFindingDecision,
    ISyncAnalysisFindingsFilters,
} from '../models/Entities/Protocols/SyncAnalysis';
import { PaginationState } from '../models/PaginationState';
import { buildHeaders, handleApiError } from './commons/CommonService.Functions';

const BaseURL =
    (process.env.REACT_APP_BILLER_API_GATEWAY || '').replace(/\/$/, '') +
    '/protocols/protocols/consumption-sync-analysis';

/** Cuántos hallazgos trae una página del listado si no se pide otro tamaño. */
export const SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE = 20;

/**
 * Análisis de una corrida (GET .../by-sync-process/{syncProcessId}).
 *
 * Devuelve null cuando la corrida no tiene análisis: no haberlo analizado es un
 * estado normal, no un error que haya que mostrar como tal.
 */
export const GetProtocolConsumptionSyncAnalysis = async (
    syncProcessId: string,
    token: string
): Promise<SyncAnalysis | null> => {
    const url = `${BaseURL}/by-sync-process/${encodeURIComponent(syncProcessId)}`;
    try {
        const response = await fetch(url, { headers: buildHeaders(token) });
        if (response.status === 404) return null;
        if (!response.ok) throw response;
        const data: IResult<SyncAnalysis> = await response.json();
        return data.data ?? null;
    } catch (error: any) {
        throw await handleApiError(error, 'GetProtocolConsumptionSyncAnalysis');
    }
};

export interface SyncAnalysisFindingsPage {
    findings: SyncAnalysisFinding[];
    paginationState: PaginationState;
}

/**
 * Hallazgos del análisis (GET .../{analysisId}/findings).
 *
 * El servidor pagina y filtra: filtrar en el cliente sólo alcanzaría a la
 * página cargada y dejaría mal los totales y el recorrido de páginas.
 */
export const GetProtocolConsumptionSyncAnalysisFindings = async (
    analysisId: string,
    token: string,
    pageNumber: number = 1,
    pageSize: number = SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE,
    filters?: ISyncAnalysisFindingsFilters
): Promise<SyncAnalysisFindingsPage> => {
    const query = new URLSearchParams();
    query.append('pageNumber', String(pageNumber));
    query.append('pageSize', String(pageSize));
    if (filters?.findingType !== undefined) query.append('findingType', String(filters.findingType));
    if (filters?.decision !== undefined) query.append('decision', String(filters.decision));
    if (filters?.onlyNew) query.append('onlyNew', 'true');

    const url = `${BaseURL}/${encodeURIComponent(analysisId)}/findings?${query.toString()}`;

    try {
        const response = await fetch(url, { headers: buildHeaders(token) });
        if (!response.ok) throw response;
        const data: IPagedResult<SyncAnalysisFinding[]> = await response.json();
        return {
            findings: data.data || [],
            paginationState: {
                pageNumber: data.pageNumber,
                pageSize: data.pageSize,
                totalPages: data.totalPages,
                totalRecords: data.totalRecords,
                firstPage: data.firstPage,
                lastPage: data.lastPage,
                nextPage: data.nextPage,
                previousPage: data.previousPage,
            },
        };
    } catch (error: any) {
        throw await handleApiError(error, 'GetProtocolConsumptionSyncAnalysisFindings');
    }
};

/**
 * Ejecuta el análisis a pedido sobre una corrida (POST .../consumption-sync-analysis).
 *
 * El análisis ya no se dispara solo al terminar la corrida: lo pide el analista
 * desde el panel, y por eso va con `automatic: false`. El endpoint es sincrónico:
 * recién responde cuando el análisis terminó, así que la espera puede ser larga.
 * Devuelve el id del análisis producido.
 *
 * Cuando el análisis falla el servidor responde 400 con el id igualmente, para
 * que se pueda ir a ver el detalle del fallo; eso llega acá como error, que es lo
 * correcto: no hubo análisis que mostrar.
 */
export const ExecuteProtocolConsumptionSyncAnalysis = async (
    syncProcessId: string,
    token: string
): Promise<string> => {
    try {
        const response = await fetch(BaseURL, {
            method: 'POST',
            headers: buildHeaders(token),
            body: JSON.stringify({ syncProcessId, automatic: false }),
        });
        if (!response.ok) throw response;
        const data: IResult<string> = await response.json();
        return data.data;
    } catch (error: any) {
        throw await handleApiError(error, 'ExecuteProtocolConsumptionSyncAnalysis');
    }
};

/**
 * Registra la decisión del analista sobre un hallazgo
 * (PUT .../findings/{findingId}/decision). Devuelve el id del hallazgo.
 */
export const SetProtocolConsumptionSyncAnalysisFindingDecision = async (
    command: SyncAnalysisFindingDecision,
    token: string
): Promise<string> => {
    const url = `${BaseURL}/findings/${encodeURIComponent(command.findingId)}/decision`;
    try {
        const response = await fetch(url, {
            method: 'PUT',
            headers: buildHeaders(token),
            body: JSON.stringify({ ...command, reason: command.reason || null }),
        });
        if (!response.ok) throw response;
        const data: IResult<string> = await response.json();
        return data.data;
    } catch (error: any) {
        throw await handleApiError(error, 'SetProtocolConsumptionSyncAnalysisFindingDecision');
    }
};
